import { Cpu, LogOut, Moon, Settings as SettingsIcon, Sun, Workflow, Zap } from "lucide-react";
import React from "react";
import { Link, useNavigate } from "react-router";
import { useIsAdmin } from "@client/hooks/useIsAdmin";
import { t } from "@client/lib/dictionary";
import { useWsSubscription } from "@client/lib/wsSubscriptions";
import { useDark, useDarkStore } from "@client/store/dark";
import { useUIStore } from "@client/store/ui";
import { useUserStore } from "@client/store/user";
import Settings from "@client/components/Settings";
import { Button } from "@client/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@client/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@client/components/ui/drawer";
import { Switch } from "@client/components/ui/switch";
import AverageValue from "@client/components/valueDisplays/elementary/AverageValue";

const POWER_TOPICS = ["zige/pozar1/12v/val", "zige/pozar0/12v/val", "zige/meteo/12v/val"];

// Sdílený WS subscription fragment — TopBar je vykreslen na všech page, takže tyhle
// 3 topicy budou aktivní vždy. Server je přidá k page-specific topicům.
const TOPBAR_WS_FRAGMENT = { mqttTopics: POWER_TOPICS };

export const TopBar: React.FC = () => {
  const chartLock = useUIStore((s) => s.chartLock);
  const toggleChartLock = useUIStore((s) => s.toggleChartLock);
  const settingsOpen = useUIStore((s) => s.settingsOpen);
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);
  const logout = useUserStore((s) => s.logout);
  const dark = useDark();
  const toggleDark = useDarkStore((s) => s.toggleDark);
  const nav = useNavigate();
  const isAdmin = useIsAdmin();
  useWsSubscription(TOPBAR_WS_FRAGMENT);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex items-center gap-3 px-3 py-2">
        <Link to="/" aria-label={t.app.name}>
          <img src="/demirel-icon.webp" alt={t.app.name} className="h-8 w-auto" />
        </Link>
        <Switch checked={chartLock} onCheckedChange={toggleChartLock} aria-label="Zamknout grafy" />
        <div className="flex-1 flex items-center">
          <AverageValue topicIds={POWER_TOPICS} />
        </div>
        <Button variant="ghost" size="icon" asChild aria-label={t.devices.title}>
          <Link to="/devices">
            <Zap className="h-5 w-5" />
          </Link>
        </Button>
        <Button variant="ghost" size="icon" asChild aria-label={t.automations.title}>
          <Link to="/automations">
            <Workflow className="h-5 w-5" />
          </Link>
        </Button>
        {/* Systémový monitor je za requireAdmin — neadminovi by odkaz vedl jen na hlášku. */}
        {isAdmin && (
          <Button variant="ghost" size="icon" asChild aria-label={t.system.title}>
            <Link to="/system">
              <Cpu className="h-5 w-5" />
            </Link>
          </Button>
        )}
        <Button variant="ghost" size="icon" onClick={() => toggleDark()} aria-label="Dark mode">
          {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
        <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} aria-label={t.dashboard.settings}>
          <SettingsIcon className="h-5 w-5" />
        </Button>
        <Button variant="ghost" size="icon" onClick={handleLogout} aria-label={t.auth.logout}>
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      {/* Settings: Drawer na mobile, Dialog na desktop */}
      {isMobile ? (
        <Drawer open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DrawerContent side="right" className="!w-full !max-w-full">
            <DrawerHeader>
              <DrawerTitle>{t.settings.title}</DrawerTitle>
            </DrawerHeader>
            <Settings closeMethod={() => setSettingsOpen(false)} />
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="!max-w-4xl">
            <DialogHeader>
              <DialogTitle>{t.settings.title}</DialogTitle>
            </DialogHeader>
            <Settings closeMethod={() => setSettingsOpen(false)} />
          </DialogContent>
        </Dialog>
      )}
    </header>
  );
};
